import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, Volume2, Loader2, Check, Mic, Trash2, Bell, Camera, MapPin, Pencil, Plus, ShieldCheck } from "lucide-react";

const TABS = [
  { key: "voice", label: "Voice & Assistant" },
  { key: "profile", label: "Profile & Pronunciation" },
  { key: "memory", label: "Memory" },
  { key: "permissions", label: "Permissions" },
  { key: "privacy", label: "Privacy" },
];

export function SettingsDialog({ open, onClose, onConversationsCleared }) {
  const { user, updateUser } = useAuth();
  const [tab, setTab] = useState("voice");
  const [voices, setVoices] = useState({ anna: [], sam: [] });
  const [vs, setVs] = useState(user.voice_settings);
  const [pref, setPref] = useState(user.assistant_preference);
  const [respLen, setRespLen] = useState(user.response_length || "balanced");
  const [notifPrefs, setNotifPrefs] = useState(user.notification_prefs || { enabled: true, quiet_enabled: false, quiet_start: 22, quiet_end: 7 });
  const [name, setName] = useState(user.preferred_name || "");
  const [hint, setHint] = useState(user.pronunciation_hint || "");
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState("");
  const [micState, setMicState] = useState("unknown");
  const [saved, setSaved] = useState(false);
  const [memories, setMemories] = useState([]);
  const [memoryEnabled, setMemoryEnabled] = useState(user.memory_enabled !== false);
  const [editingMem, setEditingMem] = useState(null);
  const [editMemVal, setEditMemVal] = useState("");
  const [newMem, setNewMem] = useState("");
  const [perms, setPerms] = useState({ microphone: "unknown", camera: "unknown", notifications: "unknown", geolocation: "unknown" });

  const loadPerms = async () => {
    const next = { microphone: "unknown", camera: "unknown", notifications: "unknown", geolocation: "unknown" };
    if (typeof Notification !== "undefined") next.notifications = Notification.permission;
    if (navigator.permissions) {
      for (const name of ["microphone", "camera", "geolocation"]) {
        try { const p = await navigator.permissions.query({ name }); next[name] = p.state; } catch {}
      }
    }
    setPerms(next);
  };

  const requestNotifications = async () => {
    if (typeof Notification === "undefined") return;
    const res = await Notification.requestPermission();
    setPerms((p) => ({ ...p, notifications: res }));
  };
  const requestCamera = async () => {
    try { const s = await navigator.mediaDevices.getUserMedia({ video: true }); s.getTracks().forEach((t) => t.stop()); setPerms((p) => ({ ...p, camera: "granted" })); }
    catch { setPerms((p) => ({ ...p, camera: "denied" })); }
  };
  const requestLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(() => setPerms((p) => ({ ...p, geolocation: "granted" })), () => setPerms((p) => ({ ...p, geolocation: "denied" })));
  };

  const addMemory = async () => {
    const v = newMem.trim(); if (!v) return;
    setNewMem("");
    const { data } = await api.post("/memories", { content: v });
    setMemories((m) => [data.memory, ...m]);
  };
  const saveMemoryEdit = async (id) => {
    const v = editMemVal.trim();
    if (v) { await api.put(`/memories/${id}`, { content: v }); setMemories((m) => m.map((x) => (x.id === id ? { ...x, content: v } : x))); }
    setEditingMem(null);
  };

  const loadMemories = async () => {
    try {
      const { data } = await api.get("/memories");
      setMemories(data.memories);
      setMemoryEnabled(data.enabled);
    } catch {}
  };

  const toggleMemory = async (val) => {
    setMemoryEnabled(val);
    const { data } = await api.put("/profile", { memory_enabled: val });
    updateUser(data.user);
  };

  const deleteMemory = async (id) => {
    await api.delete(`/memories/${id}`);
    setMemories((m) => m.filter((x) => x.id !== id));
  };

  const clearMemories = async () => {
    await api.delete("/memories");
    setMemories([]);
  };

  useEffect(() => {
    if (!open) return;
    setVs(user.voice_settings); setPref(user.assistant_preference);
    setRespLen(user.response_length || "balanced");
    setNotifPrefs(user.notification_prefs || { enabled: true, quiet_enabled: false, quiet_start: 22, quiet_end: 7 });
    setName(user.preferred_name || ""); setHint(user.pronunciation_hint || "");
    api.get("/voices").then(({ data }) => setVoices(data.voices)).catch(() => {});
    loadMemories();
    loadPerms();
    if (navigator.permissions) {
      navigator.permissions.query({ name: "microphone" }).then((p) => setMicState(p.state)).catch(() => setMicState("unknown"));
    }
  }, [open, user]);

  if (!open) return null;

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const { data } = await api.put("/profile", {
        preferred_name: name.trim(), pronunciation_hint: hint.trim(),
        assistant_preference: pref, voice_settings: vs,
        response_length: respLen, notification_prefs: notifPrefs,
      });
      updateUser(data.user);
      setSaved(true); setTimeout(() => { setSaved(false); onClose(); }, 900);
    } finally { setSaving(false); }
  };

  const preview = async (assistant, voiceId) => {
    setPreviewing(voiceId);
    try {
      const { data } = await api.post("/tts", {
        text: name.trim() ? `Hey ${name.trim()}, this is how I sound.` : "Hey, this is how I sound.",
        assistant, voice_id: voiceId,
      });
      const audio = new Audio(data.audio);
      audio.onended = () => setPreviewing("");
      await audio.play();
    } catch { setPreviewing(""); }
  };

  const requestMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicState("granted");
      setPerms((p) => ({ ...p, microphone: "granted" }));
    } catch { setMicState("denied"); setPerms((p) => ({ ...p, microphone: "denied" })); }
  };

  const clearConversations = async () => {
    const { data } = await api.get("/conversations");
    await Promise.all(data.conversations.map((c) => api.delete(`/conversations/${c.id}`)));
    onConversationsCleared && onConversationsCleared();
  };

  const VoiceOption = ({ assistant, v, selected }) => (
    <div className={`flex items-center justify-between rounded-xl px-3 py-2.5 border transition-all ${selected ? "border-mono-400/50 bg-mono-400/5" : "border-white/10"}`}>
      <button
        data-testid={`voice-option-${assistant}-${v.id}`}
        onClick={() => setVs({ ...vs, [`${assistant}_voice`]: v.id })}
        className="text-left flex-1"
      >
        <div className="text-sm flex items-center gap-2">{v.name} {selected && <Check className="w-3.5 h-3.5 text-mono-400" />}</div>
        <div className="text-xs text-slate-500">{v.desc}</div>
      </button>
      <button onClick={() => preview(assistant, v.id)} className="text-slate-400 hover:text-mono-300 ml-2" data-testid={`voice-preview-${assistant}-${v.id}`}>
        {previewing === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="settings-dialog" className="w-full max-w-2xl glass-panel rounded-3xl overflow-hidden max-h-[90vh] flex flex-col reading-page" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="font-heading text-xl font-medium">Settings</h2>
          <button data-testid="settings-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 p-4 border-b border-white/10 overflow-x-auto thin-scroll">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`settings-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap text-sm px-4 py-2 rounded-full transition-all ${tab === t.key ? "text-[#000000] font-medium" : "text-slate-400 hover:text-white"}`}
              style={tab === t.key ? { background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" } : {}}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto thin-scroll flex-1 space-y-6">
          {tab === "voice" && (
            <>
              <div>
                <label className="label-mono">Preferred assistant</label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {["anna", "sam"].map((a) => (
                    <button key={a} data-testid={`pref-assistant-${a}`} onClick={() => setPref(a)}
                      className={`rounded-xl py-3 border text-sm capitalize transition-all ${pref === a ? "border-mono-400/50 bg-mono-400/5 text-mono-200" : "border-white/10 text-slate-300"}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-mono">Anna's voice</label>
                <div className="space-y-2 mt-2">
                  {voices.anna?.map((v) => <VoiceOption key={v.id} assistant="anna" v={v} selected={vs.anna_voice === v.id} />)}
                </div>
              </div>
              <div>
                <label className="label-mono">Sam's voice</label>
                <div className="space-y-2 mt-2">
                  {voices.sam?.map((v) => <VoiceOption key={v.id} assistant="sam" v={v} selected={vs.sam_voice === v.id} />)}
                </div>
              </div>
              <div>
                <label className="label-mono">Speaking speed · {vs.speed?.toFixed(2)}x</label>
                <input type="range" min="0.7" max="1.2" step="0.05" value={vs.speed}
                  data-testid="settings-speed-slider"
                  onChange={(e) => setVs({ ...vs, speed: parseFloat(e.target.value) })}
                  className="w-full mt-3 accent-mono-400" />
              </div>
              <label className="flex items-center justify-between rounded-xl px-4 py-3 border border-white/10">
                <span className="text-sm">Auto-speak responses</span>
                <input type="checkbox" data-testid="settings-autospeak-toggle" checked={vs.auto_speak}
                  onChange={(e) => setVs({ ...vs, auto_speak: e.target.checked })} className="w-4 h-4 accent-mono-400" />
              </label>
              <div>
                <label className="label-mono">Response length</label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[{ k: "short", l: "Short" }, { k: "balanced", l: "Balanced" }, { k: "detailed", l: "Detailed" }].map((o) => (
                    <button key={o.k} data-testid={`response-length-${o.k}`} onClick={() => setRespLen(o.k)}
                      className={`rounded-xl py-2.5 border text-sm transition-all ${respLen === o.k ? "border-mono-400/50 bg-mono-400/5 text-mono-200" : "border-white/10 text-slate-300"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">How much detail Anna and Sam give by default. They'll still adapt to the moment.</p>
              </div>
            </>
          )}

          {tab === "profile" && (
            <>
              <div>
                <label className="label-mono">Preferred name</label>
                <input data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full rounded-xl bg-[#0D0D0D] border border-white/10 px-4 py-3 text-sm outline-none focus:border-mono-400/50" />
              </div>
              <div className="flex items-center gap-3">
                <button data-testid="pronunciation-preview-button" onClick={() => preview(pref, vs[`${pref}_voice`])}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 glass-card text-mono-300 text-sm hover:border-mono-400/50">
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />} Preview name
                </button>
              </div>
              <div>
                <label className="label-mono">Pronunciation hint</label>
                <input data-testid="settings-pronunciation-input" value={hint} onChange={(e) => setHint(e.target.value)}
                  placeholder="Spell your name the way it should sound"
                  className="mt-2 w-full rounded-xl bg-[#0D0D0D] border border-white/10 px-4 py-3 text-sm outline-none focus:border-mono-400/50" />
                <p className="text-xs text-slate-500 mt-2">Only needed if the default pronunciation is off. This changes how your name sounds, not how it's shown.</p>
              </div>
            </>
          )}

          {tab === "memory" && (
            <>
              <div className="flex items-center justify-between rounded-xl px-4 py-3 border border-white/10">
                <div>
                  <div className="text-sm">Conversation memory</div>
                  <div className="text-xs text-slate-500 mt-1">When on, Anna and Sam remember useful context (projects, goals, preferences) across conversations.</div>
                </div>
                <input type="checkbox" data-testid="memory-toggle" checked={memoryEnabled}
                  onChange={(e) => toggleMemory(e.target.checked)} className="w-4 h-4 accent-mono-400" />
              </div>
              <div className="flex gap-2">
                <input data-testid="memory-add-input" value={newMem} onChange={(e) => setNewMem(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addMemory()} placeholder="Add something to remember…"
                  className="flex-1 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50" />
                <button data-testid="memory-add-button" onClick={addMemory} className="w-10 h-10 rounded-xl flex items-center justify-center text-[#000000]" style={{ background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" }}><Plus className="w-4 h-4" /></button>
              </div>
              <div className="space-y-2" data-testid="memory-list">
                {memories.length === 0 && <div className="text-xs text-slate-500">Nothing remembered yet.</div>}
                {memories.map((m) => (
                  <div key={m.id} data-testid="memory-item" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 bg-white/5">
                    {editingMem === m.id ? (
                      <input autoFocus data-testid="memory-edit-input" value={editMemVal} onChange={(e) => setEditMemVal(e.target.value)}
                        onBlur={() => saveMemoryEdit(m.id)} onKeyDown={(e) => e.key === "Enter" && saveMemoryEdit(m.id)}
                        className="flex-1 bg-transparent border-b border-mono-400/50 text-sm outline-none text-slate-100" />
                    ) : (
                      <span className="text-sm text-slate-200 flex-1">{m.content}</span>
                    )}
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid="memory-edit" onClick={() => { setEditingMem(m.id); setEditMemVal(m.content); }} className="text-slate-500 hover:text-mono-300"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid="memory-delete" onClick={() => deleteMemory(m.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
              {memories.length > 0 && (
                <button data-testid="memory-clear-all" onClick={clearMemories} className="inline-flex items-center gap-2 text-sm text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" /> Clear all memories
                </button>
              )}
              <div className="text-xs text-slate-500">We never store sensitive info (passwords, financial or health details) as memory.</div>
            </>
          )}

          {tab === "permissions" && (
            <>
              <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="w-4 h-4 text-mono-300" /> Every sensitive permission is off until you explicitly enable it. Nothing is activated secretly.</div>
              {[
                { key: "microphone", label: "Microphone", icon: Mic, req: requestMic, used: "Used for voice input." },
                { key: "notifications", label: "Notifications", icon: Bell, req: requestNotifications, used: "Used for reminders." },
                { key: "camera", label: "Camera", icon: Camera, req: requestCamera, used: "Not used yet — reserved for a later phase." },
                { key: "geolocation", label: "Location", icon: MapPin, req: requestLocation, used: "Not used yet — reserved for a later phase." },
              ].map((p) => {
                const Icon = p.icon; const state = perms[p.key];
                return (
                  <div key={p.key} data-testid={`permission-${p.key}`} className="rounded-xl px-4 py-3 border border-white/10 flex items-center justify-between">
                    <div>
                      <div className="text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-mono-300" /> {p.label}</div>
                      <div className="text-xs text-slate-500 mt-1">{p.used} Status: <span className="capitalize">{state}</span></div>
                    </div>
                    {state !== "granted" && (
                      <button data-testid={`permission-enable-${p.key}`} onClick={p.req} className="text-sm text-mono-300 hover:underline">Enable</button>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {tab === "privacy" && (
            <>
              <div className="rounded-xl px-4 py-4 border border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm flex items-center gap-2"><Bell className="w-4 h-4 text-mono-300" /> Proactive notifications</div>
                    <div className="text-xs text-slate-500 mt-1">Reminders and automations alert you here. Turn off to stay silent.</div>
                  </div>
                  <input type="checkbox" data-testid="notif-enabled-toggle" checked={notifPrefs.enabled}
                    onChange={(e) => setNotifPrefs({ ...notifPrefs, enabled: e.target.checked })} className="w-4 h-4 accent-mono-400" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Quiet hours</span>
                  <input type="checkbox" data-testid="notif-quiet-toggle" checked={notifPrefs.quiet_enabled}
                    onChange={(e) => setNotifPrefs({ ...notifPrefs, quiet_enabled: e.target.checked })} className="w-4 h-4 accent-mono-400" />
                </div>
                {notifPrefs.quiet_enabled && (
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    From <select data-testid="notif-quiet-start" value={notifPrefs.quiet_start} onChange={(e) => setNotifPrefs({ ...notifPrefs, quiet_start: parseInt(e.target.value) })} className="rounded-lg bg-[#0D0D0D] border border-white/10 px-2 py-1">
                      {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{h}:00</option>)}
                    </select>
                    to <select data-testid="notif-quiet-end" value={notifPrefs.quiet_end} onChange={(e) => setNotifPrefs({ ...notifPrefs, quiet_end: parseInt(e.target.value) })} className="rounded-lg bg-[#0D0D0D] border border-white/10 px-2 py-1">
                      {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{h}:00</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="rounded-xl px-4 py-4 border border-white/10">
                <div className="text-sm">Conversation history</div>
                <div className="text-xs text-slate-500 mt-1">Your conversations are stored so Anna and Sam remember context. Only you can see them.</div>
                <button data-testid="privacy-clear-conversations" onClick={clearConversations}
                  className="mt-3 inline-flex items-center gap-2 text-sm text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" /> Delete all conversations
                </button>
              </div>
              <div className="rounded-xl px-4 py-4 border border-white/10 text-xs text-slate-500">
                Your data is isolated to your account — no one else can access your conversations, memories, tasks, reminders, notes or automations. API keys stay on the server and are never exposed to the browser.
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-white/10 flex justify-end">
          <button data-testid="settings-save" onClick={save} disabled={saving}
            className="rounded-full px-6 py-2.5 text-sm font-medium text-[#000000] flex items-center gap-2 disabled:opacity-60"
            style={{ background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
            {saved ? "Saved" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
